/**
 * @file CartColorsGroup.tsx
 * @description Группа «Цвета» «Вида корзины» (phase19.16, SB120 этап 4;
 * uiux-cart §4–§5, §7; db-design-sb120-stage4 §1.1–§1.2):
 *  - заливка корзины — первая ячейка «Как шапка» (`fill: null`), роли,
 *    пипетка; ↺ — роль «Тёмный фон», как сейчас; кружок «Как шапка» —
 *    цветом заливки шапки; контраст — только у поля текста;
 *  - цвет текста и цен — «Авто» (`{ kind: "auto" }`, по заливке), роли,
 *    пипетка; контраст с заливкой и «Взять читаемый» — у поля;
 *  - «Итого»: заливка (↺ «Фон страницы») и цвет суммы (↺ «Акцент»).
 * При «Как шапка» и прозрачной шапке — подсказка, что корзина возьмёт
 * заливку шапки после прокрутки. Кнопка «Оформить заказ» — кнопка темы:
 * ссылка в «Оформление».
 */

import { DEFAULT_SITE_CART, resolveSurfaceFill, SITE_FILL_ROLES, SITE_TEXT_COLOR_ROLES } from "@chaiberi/shared";
import { Link } from "react-router";

import { FieldHint, SettingsGroup, SettingsSubgroup } from "../../ui/settings-groups";
import { ColorField } from "../../site-fields/ColorField";
import { useCartLook } from "./cart-look-context";
import { CartFields } from "./CartFields";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { CART_TRANSPARENT_HEADER_TEXT, cartSummaryBg, cartTextBg, colorsSummary, groupHasWarning, sameValue } from "../../../lib/site-view/cart-look";
import { TEXT_COLOR_ROLE_LABELS } from "../../../lib/site-fields/text-color-labels";
import { FILL_ROLE_LABELS } from "../../../lib/site-view/surface-fill-labels";

export function CartColorsGroup() {
  const { cart, theme, warnings, change } = useCartLook();
  const link = useBuilderLink();
  const D = DEFAULT_SITE_CART;
  const colors = theme.colors;
  /** «↺» только у изменённого: возврат к виду «как сейчас». */
  const reset = <K extends "fill" | "text" | "summaryFill" | "totalColor">(key: K) =>
    sameValue(cart[key], D[key]) ? undefined : () => change({ [key]: D[key] });

  return (
    <SettingsGroup id="colors" title="Цвета" hint="Заливка корзины, текст и «Итого»." summary={colorsSummary(cart)} status={groupHasWarning(warnings, "colors") ? "warning" : undefined}>
      <CartFields>
        <ColorField
          mode="fill"
          label="Заливка корзины"
          value={cart.fill}
          none={{ label: "Как шапка", swatch: resolveSurfaceFill(theme.header.fill, theme).background }}
          roles={SITE_FILL_ROLES}
          labels={FILL_ROLE_LABELS}
          colors={colors}
          // Контраст текста на заливке — у поля «Цвет текста и цен» ниже, здесь не повторяем.
          contrast={false}
          inheritLabel="как было"
          onReset={reset("fill")}
          onChange={(fill) => change({ fill })}
        />
        {cart.fill === null && theme.header.tone === "transparent" ? <FieldHint className="mt-0">{CART_TRANSPARENT_HEADER_TEXT}</FieldHint> : null}
        <ColorField
          mode="text"
          label="Цвет текста и цен"
          value={cart.text}
          roles={SITE_TEXT_COLOR_ROLES}
          labels={TEXT_COLOR_ROLE_LABELS}
          colors={colors}
          elementBg={cartTextBg(cart, theme)}
          onReset={reset("text")}
          onChange={(text) => change({ text })}
        />
        <SettingsSubgroup title="«Итого»">
          <ColorField
            mode="fill"
            label="Заливка «Итого»"
            value={cart.summaryFill}
            roles={SITE_FILL_ROLES}
            labels={FILL_ROLE_LABELS}
            colors={colors}
            inheritLabel="как было"
            onReset={reset("summaryFill")}
            onChange={(fill) => (fill ? change({ summaryFill: fill }) : undefined)}
          />
          <ColorField
            mode="text"
            label="Цвет суммы"
            value={cart.totalColor}
            roles={SITE_TEXT_COLOR_ROLES}
            labels={TEXT_COLOR_ROLE_LABELS}
            colors={colors}
            elementBg={cartSummaryBg(cart, theme)}
            inheritLabel="как было"
            onReset={reset("totalColor")}
            onChange={(totalColor) => change({ totalColor })}
          />
        </SettingsSubgroup>
        <FieldHint className="mt-0">Линии у кнопок −/+ и пустая корзина — по тону заливки, отдельной настройки нет.</FieldHint>
      </CartFields>
      <Link to={link({ panel: "appearance", sub: "shapes" })} className="type-body inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
        Форма и цвет кнопок — в «Оформлении» →
      </Link>
    </SettingsGroup>
  );
}
